<template>
  <section class="sidebar-card">
    <div v-if="!only" class="tools-head">
      <div class="card-eyebrow">Tools</div>
    </div>

    <!-- ============ Slice / Cut-away ============ -->
    <template v-if="showSlice">
    <button type="button" class="tool-toggle" :class="{ 'is-static': only }" :aria-expanded="only==='slice' || sliceOpen"
      @click="only ? null : (sliceOpen = !sliceOpen)">
      <span class="tool-name">Slice / Cut-away</span>
      <v-icon v-if="!only" size="20" color="#8aa0aa">{{ sliceOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </button>

    <div v-show="only==='slice' || sliceOpen" class="tool-body">
      <p v-if="!hasBounds" class="tool-hint">Load a model to slice through it.</p>

      <template v-else>
        <div class="slice-top">
          <v-switch v-model="enabled" color="#64ffda" density="compact" hide-details inset
            :label="enabled ? 'Slice on' : 'Slice off'" @update:model-value="onToggle" />
          <button type="button" class="reset-btn" :disabled="!enabled" title="Reset to full extent" @click="reset">
            <v-icon size="15">mdi-restore</v-icon>
            <span>Reset</span>
          </button>
        </div>

        <p class="tool-hint">Drag an axis' handles to cut the cloud down. Lower an axis' max to take the roof off.</p>

        <div v-for="axis in axes" :key="axis.key" class="axis-row" :class="{ 'is-disabled': !enabled }">
          <div class="axis-head">
            <span class="axis-label">{{ axis.key }}</span>
            <span class="axis-values">{{ fmt(model[axis.key][0]) }} — {{ fmt(model[axis.key][1]) }}</span>
          </div>
          <v-range-slider v-model="model[axis.key]" :min="ranges[axis.key][0]" :max="ranges[axis.key][1]"
            :step="steps[axis.key]" :disabled="!enabled" color="#64ffda" track-color="#33474f"
            density="compact" hide-details strict @update:model-value="onSlide" />
        </div>
      </template>
    </div>
    </template>

    <div v-if="!only" class="tool-divider"></div>

    <!-- ============ Walkthrough playback ============ -->
    <template v-if="showWalk">
    <button type="button" class="tool-toggle" :class="{ 'is-static': only }" :aria-expanded="only==='walk' || playbackOpen"
      @click="only ? null : (playbackOpen = !playbackOpen)">
      <span class="tool-name">Walkthrough playback</span>
      <v-icon v-if="!only" size="20" color="#8aa0aa">{{ playbackOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
    </button>

    <div v-show="only==='walk' || playbackOpen" class="tool-body">
      <p v-if="!available" class="tool-hint">Load a model with a capture path to fly through it.</p>

      <template v-else>
        <p class="tool-hint">Fly the camera along the capture route. Orbit any time — the view holds its
          direction when you let go. Spacebar plays/pauses.</p>

        <label v-if="scans.length > 1" class="pb-field">
          <span class="pb-field-label">Scan path</span>
          <select class="pb-select" :value="selectedScanId" @change="$emit('selectScan', $event.target.value)">
            <option v-for="s in scans" :key="s.scanId" :value="s.scanId">{{ s.name }}</option>
          </select>
        </label>

        <div class="pb-transport">
          <button type="button" class="pb-btn primary" :title="isPlaying ? 'Pause' : 'Play'" @click="$emit('toggle')">
            <v-icon size="20">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
          </button>
          <button type="button" class="pb-btn" title="Stop" :disabled="!playing" @click="$emit('stop')">
            <v-icon size="18">mdi-stop</v-icon>
          </button>

          <label class="pb-field pb-speed-field">
            <span class="pb-field-label">Speed</span>
            <select class="pb-select" :value="speed" @change="$emit('speed', $event.target.value)">
              <option :value="2">2×</option>
              <option :value="1">1×</option>
              <option :value="0.75">0.75×</option>
              <option :value="0.5">0.5×</option>
              <option :value="0.25">0.25×</option>
            </select>
          </label>
        </div>

        <input class="pb-seek" type="range" min="0" max="1" step="0.001" :value="progress"
          @input="$emit('seek', parseFloat($event.target.value))" />
      </template>
    </div>
    </template>
  </section>
</template>

<script>
export default {
  name: 'ModelToolsCard',
  props: {
    // Slice: world-space model bounds { min:{x,y,z}, max:{x,y,z} } — null until a model loads.
    bounds: { type: Object, default: null },
    // Playback: snapshot from ModelViewer's onPlaybackState.
    playbackState: { type: Object, default: null },
    // When set ('slice' | 'walk'), render just that one tool section, forced open and without the
    // "Tools" eyebrow/chevron chrome — used when the section is hosted in the viewer tool rail.
    only: { type: String, default: null },
  },
  emits: ['apply', 'clear', 'toggle', 'stop', 'seek', 'speed', 'selectScan'],
  data() {
    return {
      sliceOpen: false,
      playbackOpen: false,
      // Slice state
      enabled: false,
      axes: [{ key: 'x' }, { key: 'y' }, { key: 'z' }],
      model: { x: [0, 1], y: [0, 1], z: [0, 1] },
    };
  },
  computed: {
    showSlice() { return !this.only || this.only === 'slice'; },
    showWalk() { return !this.only || this.only === 'walk'; },
    // ----- Slice -----
    hasBounds() {
      return !!(this.bounds && this.bounds.min && this.bounds.max);
    },
    ranges() {
      const b = this.bounds;
      const range = (lo, hi) => (hi > lo ? [lo, hi] : [lo, lo + 1]);
      if (!this.hasBounds) return { x: [0, 1], y: [0, 1], z: [0, 1] };
      return { x: range(b.min.x, b.max.x), y: range(b.min.y, b.max.y), z: range(b.min.z, b.max.z) };
    },
    steps() {
      const step = ([lo, hi]) => (hi - lo) / 200 || 0.01;
      return { x: step(this.ranges.x), y: step(this.ranges.y), z: step(this.ranges.z) };
    },
    box() {
      return {
        min: { x: this.model.x[0], y: this.model.y[0], z: this.model.z[0] },
        max: { x: this.model.x[1], y: this.model.y[1], z: this.model.z[1] },
      };
    },
    // ----- Playback -----
    available() { return !!this.playbackState?.available; },
    playing() { return !!this.playbackState?.playing; },
    paused() { return !!this.playbackState?.paused; },
    isPlaying() { return this.playing && !this.paused; },
    progress() { return this.playbackState?.progress ?? 0; },
    speed() { return this.playbackState?.speed ?? 1; },
    scans() { return this.playbackState?.scans ?? []; },
    selectedScanId() { return this.playbackState?.selectedScanId ?? null; },
  },
  watch: {
    // New model → reset the slice handles so we never cut with stale coordinates.
    bounds: {
      immediate: true,
      handler() {
        this.resetHandles();
        if (this.enabled) this.$emit('apply', this.box);
      },
    },
  },
  methods: {
    fmt(v) {
      if (v == null || Number.isNaN(v)) return '—';
      return Math.abs(v) >= 100 ? v.toFixed(0) : Number(v.toPrecision(3)).toString();
    },
    resetHandles() {
      this.model = { x: [...this.ranges.x], y: [...this.ranges.y], z: [...this.ranges.z] };
    },
    onToggle(v) {
      if (v) this.$emit('apply', this.box);
      else this.$emit('clear');
    },
    onSlide() {
      if (this.enabled) this.$emit('apply', this.box);
    },
    reset() {
      this.resetHandles();
      if (this.enabled) this.$emit('apply', this.box);
    },
  },
};
</script>

<style scoped>
.sidebar-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tools-head {
  padding: 12px 14px 6px;
}

.card-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8aa0aa;
}

/* Each tool is a collapsible chevron row inside the one Tools card. */
.tool-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  padding: 8px 14px;
}

.tool-toggle:hover .tool-name { color: var(--duallo-accent); }

/* When hosted solo in the rail, the header is just a static title (the rail icon is the toggle). */
.tool-toggle.is-static { cursor: default; padding-top: 12px; }
.tool-toggle.is-static:hover .tool-name { color: #ffffff; }

.tool-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
}

.tool-body {
  padding: 2px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tool-divider {
  height: 1px;
  margin: 2px 14px;
  background: rgba(255, 255, 255, 0.06);
}

.tool-hint {
  font-size: 0.78rem;
  color: #8aa0aa;
  line-height: 1.45;
  margin: 0;
}

/* ----- Slice ----- */
.slice-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfd8dc;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.reset-btn:hover:not(:disabled) {
  background: rgba(100, 255, 218, 0.08);
  border-color: rgba(100, 255, 218, 0.3);
  color: #fff;
}

.reset-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.axis-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  transition: opacity 0.15s ease;
}

.axis-row.is-disabled { opacity: 0.5; }

.axis-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.axis-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #cfd8dc;
  text-transform: uppercase;
}

.axis-values {
  font-size: 0.74rem;
  color: #8aa0aa;
  font-variant-numeric: tabular-nums;
}

/* ----- Playback ----- */
.pb-field { display: flex; flex-direction: column; gap: 4px; }

.pb-field-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8aa0aa;
}

/* Dark dropdowns with white text (options too, for the native list). */
.pb-select {
  background: #0f171b;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  padding: 7px 9px;
  font: inherit;
  font-size: 0.82rem;
  cursor: pointer;
}

.pb-select:focus { outline: none; border-color: rgba(100, 255, 218, 0.5); }
.pb-select option { background: #0f171b; color: #ffffff; }

.pb-transport {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.pb-speed-field { margin-left: auto; }
.pb-speed-field .pb-select { min-width: 82px; }

.pb-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #e3eef0;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.pb-btn:hover:not(:disabled) {
  background: rgba(100, 255, 218, 0.14);
  border-color: rgba(100, 255, 218, 0.45);
  color: #fff;
}

.pb-btn.primary {
  background: rgba(100, 255, 218, 0.18);
  border-color: rgba(100, 255, 218, 0.55);
  color: #fff;
}

.pb-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.pb-seek { width: 100%; accent-color: var(--duallo-accent); cursor: pointer; }

</style>
